/**
 * HomeScreen — the default home for wow crew, managers, and DMs.
 * -------------------------------------------------------------------------
 * Locked in 2026-08-07 (was HomePageV2Preview.js). See the checked-in
 * commit history for the design journey; highlights:
 *   • Stories at the top (social first)
 *   • Role-aware hero: manager "Right now" scoreboard, DM district
 *     rollup, crew personal capture card.
 *   • Header cluster: Inbox / % / Bell / Sound / Avatar — matches V1.
 *   • Bell items route by type (see handleNotificationClick).
 *   • Lightweight pages (Passcode, Tasks, Penny Round) open as
 *     bottom-sheet overlays. Heavier pages (Zone Sheet, Mgr Dash,
 *     District, LP) still full-page for now.
 * Legacy V1 home stays reachable at `?legacy=1` for one week as a
 * safety net.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { trackView } from "../lib/usage";
import {
  ClipboardText, EnvelopeSimple, ChartLineUp, Users, Trophy,
  CalendarDots, Buildings, ArrowRight, TrendUp, Clock,
  Sparkle, Warning, Plus, ChatCircleDots,
  ListChecks, LockKey, Broadcast, Crown, Storefront, CaretUp, CaretDown,
  ChatsCircle, Bell, SpeakerHigh, SpeakerSlash, Confetti, HandHeart,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { ProfileMenuButton } from "./HomePage";
import { NotificationsPanel } from "./ManagerDashboard";
import CouponHeaderPill from "../components/CouponHeaderPill";
import InstallAppPrompt from "../components/InstallAppPrompt";
import PushNudge from "../components/PushNudge";
import PushHardGate from "../components/PushHardGate";
import SetupNudgeCard from "../components/SetupNudgeCard";
import WalkieFAB from "../components/WalkieFAB";
import ReportIssueModal from "../components/ReportIssueModal";
import EODReportModal from "../components/EODReportModal";
import YourTasksTodayCard from "../components/YourTasksTodayCard";
import DailyPasscodeCard from "../components/DailyPasscodeCard";
import StoriesRow from "../components/StoriesRow";
import formatLastLogin from "../utils/formatLastLogin";
import CrewReportCardDialog from "../components/CrewReportCardDialog";
import pkg from "../../package.json";
import { getApiStoreId } from "../lib/apiScope";
import { routeNotification } from "../lib/notificationRouter";
import { useTheme } from "../ThemeContext";
import PitchBlackHero from "../components/PitchBlackHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const greetingText = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const fmtMoney = (n) => (n == null ? "—" : `$${Math.round(Number(n)).toLocaleString("en-US")}`);

export default function HomeScreen({ user, crew = [], onNavigate, onSwitchUser, onOpenSheet, birthday, onOpenBirthday, belatedCards = [], onOpenBelated, onOpenKeepsake }) {
  const { theme: activeTheme } = useTheme();
  const isPitchBlack = (
    (activeTheme?.primary_color || "").toLowerCase() === "#ec008c" &&
    (activeTheme?.accent_color || "").toLowerCase() === "#a3e635"
  );
  const [tally, setTally] = useState(null);
  const [biz, setBiz] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [tasksSummary, setTasksSummary] = useState({ done: 0, total: 0, inProgress: 0 });
  const [passcodeReady, setPasscodeReady] = useState(false);
  const [dmRollup, setDmRollup] = useState(null); // live district rollup for DM hero
  const [dmUnread, setDmUnread] = useState(0);    // real DM alerts feed (unread notifs)
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showFloorDialog, setShowFloorDialog] = useState(false);
  // Which crew row is currently expanded in the "Who's on the Floor"
  // Game Plan dialog. `""` = collapsed (show list only).
  const [floorExpandedCrewId, setFloorExpandedCrewId] = useState("");
  // Crew member whose report card is currently open (managers/admins
  // only — see check on the row click). User ask 2026-02-26: "make
  // every team member clickable with their report card to see tasks,
  // goals and in progress, messages".
  const [reportCardCrewId, setReportCardCrewId] = useState("");
  const [inboxUnread, setInboxUnread] = useState(0);
  const [cueMuted, setCueMuted] = useState(() => {
    try { return localStorage.getItem("ecDashCueMuted") === "1"; } catch { return false; }
  });
  const [now, setNow] = useState(new Date());
  // Yesterday's tally + biz for the real "vs yesterday" delta on the
  // Right Now scoreboard (replaced the hardcoded `+8` mock 2026-02-26
  // after user reported "email capture is wrong"). We fetch it once at
  // mount alongside today's data. Missing / not-yet-open days quietly
  // resolve to null so the UI just hides the delta chip instead of
  // fabricating a number.
  const [yTally, setYTally] = useState(null);
  const [yBiz, setYBiz] = useState(null);
  // Store hours — pulled once per mount so the "Next" tile can point at
  // the real close time / checkpoint times instead of the hardcoded
  // 2/5/8 PM defaults. Falls back to those defaults if settings didn't
  // load. (User report 2026-08-07: banner said "Store close in 0 min"
  // at 9:04 PM when the store closes at 9:30.)
  const [effHours, setEffHours] = useState(null);
  // Contest state — decides whether to promote the Contest pill to a
  // hero tile during active contest weeks. `tile_visible` is the source
  // of truth (set by /api/contest/may-2026/toggle). We also expose
  // total_tickets + days_left so the tile can double as a scoreboard.
  const [contestState, setContestState] = useState(null);

  // MST-anchored "today" — MUST NOT use `toISOString().slice(0,10)` here
  // because that's UTC. At 9 PM MST, UTC is already the next day, so the
  // Right Now tile would silently query TOMORROW's empty tally and reset
  // to zeros the moment the clock crossed midnight UTC (~5 PM MST).
  // (User bug 2026-02-27: "Right Now is resetting right when the store
  // closes rather than at 12am".) America/Denver keeps the day rolling
  // at real local midnight regardless of DST — matches how CrewTallyPage
  // and the backend WTD rollup key their days.
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
  // Real role from the signed-in user. No more preview switcher — V2 is
  // the default home as of 2026-08-06.
  const role = user?.is_district_manager ? "dm" : (user?.is_manager || user?.is_admin) ? "manager" : "crew";
  const isManager = role === "manager";
  const isDM = role === "dm";
  const isCrew = role === "crew";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 2026-02-26 fix — `/api/zone-sheets/{date}` requires actor_id
        // (401 otherwise). We were relying on the axios interceptor,
        // which only stamps `store_id`, so this call silently 401'd and
        // `sheet` stayed null. That's why the Home tile showed "0 wow
        // crew" even with a full team on the schedule while the Zone
        // Sheet page (which passes actor_id explicitly) rendered 9.
        const actorId = user?.crew_id || user?.id || "";
        const scopeParams = actorId ? { params: { actor_id: actorId } } : undefined;
        // Yesterday key for the "vs yesterday" delta — MST-anchored to
        // match the `today` above. Same rationale: UTC-slice at 9 PM MST
        // would roll two days forward and the "vs yesterday" delta would
        // be comparing today vs today.
        const yDate = new Date();
        yDate.setDate(yDate.getDate() - 1);
        const y = yDate.toLocaleDateString("en-CA", { timeZone: "America/Denver" });
        // 2026-02-26 fix — switch capture data source from
        // `/api/tallies/{date}` (raw crew_counts) to `/api/tally/{date}`
        // (pre-computed total_emails + store_transactions + capture_rate
        // + latest_checkpoint). That's the same endpoint the Store
        // Progress card on CrewTallyPage uses, so the Right Now tile
        // stops disagreeing with it by 88 percentage points (user
        // report 2026-02-26: "Data still doesn't match! 89.9% right
        // now on email capture yesterday we finished with 43.2%").
        const [t, b, z, yt, yb] = await Promise.all([
          axios.get(`${API}/tally/${today}`).catch(() => ({ data: null })),
          axios.get(`${API}/biz/${today}`).catch(() => ({ data: null })),
          axios.get(`${API}/zone-sheets/${today}`, scopeParams).catch(() => ({ data: null })),
          axios.get(`${API}/tally/${y}`).catch(() => ({ data: null })),
          axios.get(`${API}/biz/${y}`).catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setTally(t.data); setBiz(b.data); setSheet(z.data);
          setYTally(yt.data); setYBiz(yb.data);
        }
      } catch { /* silent — mockup falls back gracefully */ }
    })();
    const int = setInterval(() => setNow(new Date()), 30000);
    return () => { cancelled = true; clearInterval(int); };
  }, [today, user?.crew_id, user?.id]);

  // ---- Tasks + Passcode summary (same endpoints HomePage uses) ---------
  useEffect(() => {
    if (!user?.crew_id) return undefined;
    let cancelled = false;
    const todayLocal = new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
    (async () => {
      try {
        const r = await axios.get(`${API}/zone-sheets/my/${todayLocal}`, {
          params: { actor_id: user.crew_id, store_id: getApiStoreId() || user.home_store_id || "" },
        });
        if (cancelled) return;
        const tasks = Array.isArray(r.data?.tasks) ? r.data.tasks : [];
        const done = tasks.filter((t) => t.status === "completed" || t.status === "manager_verified").length;
        const inProgress = tasks.filter((t) => t.status === "in_progress" || t.status === "awaiting_verify").length;
        setTasksSummary({ done, total: tasks.length, inProgress });
      } catch { if (!cancelled) setTasksSummary({ done: 0, total: 0, inProgress: 0 }); }
      try {
        const r = await axios.get(`${API}/passcodes/me`, { params: { crew_id: user.crew_id } });
        if (cancelled) return;
        setPasscodeReady(!!(r.data && r.data.code));
      } catch { if (!cancelled) setPasscodeReady(false); }
    })();
    return () => { cancelled = true; };
  }, [user?.crew_id, user?.home_store_id]);

  // ---- DM district live rollup — fetched for actual DMs so the hero
  // renders real numbers. 403s silently fall back to zero-state. -------
  useEffect(() => {
    if (!isDM || !user?.crew_id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/district/live-rollup`, {
          params: { actor_id: user.crew_id, date: today },
        });
        if (!cancelled) setDmRollup(r.data);
      } catch { if (!cancelled) setDmRollup(null); }
    })();
    return () => { cancelled = true; };
  }, [isDM, user?.crew_id, today]);

  // ---- Real DM alerts feed — count of unread notifications in the
  // manager bell scope. Replaces the checkpoint-only heuristic so the
  // banner reflects everything the bell knows about. -------------------
  useEffect(() => {
    if (!isDM || !user?.crew_id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/notifications/unread-count`, {
          params: { crew_id: user.crew_id, is_manager: true, store_id: getApiStoreId() || user.home_store_id || "" },
        });
        if (!cancelled) setDmUnread(Number(r.data?.count || 0));
      } catch { if (!cancelled) setDmUnread(0); }
    })();
    return () => { cancelled = true; };
  }, [isDM, user?.crew_id, user?.home_store_id]);


  // ---- Store hours for the "Next" tile — must respect per-day and
  // per-store overrides. /api/settings?store_id= gives us
  // `effective_today` which already resolves all layered overrides.
  useEffect(() => {
    if (!user?.home_store_id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/settings`, {
          params: { store_id: getApiStoreId() || user.home_store_id },
        });
        if (!cancelled) setEffHours(r.data?.effective_today || null);
      } catch { /* silent — falls back to hardcoded defaults */ }
    })();
    return () => { cancelled = true; };
  }, [user?.home_store_id]);

  // ---- Contest state — powers the "hero tile during active weeks"
  // promotion. Falls back to null (Contest stays a pill) if the fetch
  // fails or the contest isn't currently featured. --------------------
  useEffect(() => {
    if (!user?.home_store_id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/contest/may-2026`, {
          params: { store_id: getApiStoreId() || user.home_store_id },
        });
        if (!cancelled) setContestState(r.data || null);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user?.home_store_id]);

  // ---- Bell feed for the profile-menu Notifications row — full list so
  // NotificationsPanel can render + mark-as-read. Poll every 45s. --------
  useEffect(() => {
    if (!user?.crew_id) return undefined;
    let cancelled = false;
    const isMgr = !!(user?.is_manager || user?.is_admin || user?.is_district_manager);
    const load = async () => {
      try {
        const r = await axios.get(`${API}/notifications`, {
          params: { crew_id: user.crew_id, is_manager: String(isMgr), limit: 30, store_id: user.home_store_id || "" },
        });
        if (!cancelled) setNotifications(Array.isArray(r.data) ? r.data : []);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 45000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.crew_id, user?.home_store_id, user?.is_manager, user?.is_admin, user?.is_district_manager]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Inbox unread count — same endpoint HomePage uses so the pill badge
  // stays in lockstep with the messages page. Polls at 45s.
  useEffect(() => {
    if (!user?.crew_id) return undefined;
    let cancelled = false;
    const isMgr = !!(user?.is_manager || user?.is_admin || user?.is_district_manager);
    const load = async () => {
      try {
        const r = await axios.get(`${API}/inbox/${user.crew_id}/unread-count`, {
          params: { is_manager: isMgr },
        });
        if (!cancelled) setInboxUnread(Number(r.data?.count || 0));
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 45000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.crew_id, user?.is_manager, user?.is_admin, user?.is_district_manager]);

  const toggleCueMuted = () => {
    setCueMuted((m) => {
      const next = !m;
      try { localStorage.setItem("ecDashCueMuted", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const markAllRead = async () => {
    if (!user?.crew_id) return;
    const isMgr = !!(user?.is_manager || user?.is_admin || user?.is_district_manager);
    // URLSearchParams (not axios `params`) so we match the exact query
    // shape V1 uses. Store_id intentionally omitted here — the endpoint
    // resolves it from the manager's home store. Log errors instead of
    // swallowing so a broken mark-all doesn't fail silently. (User
    // report 2026-08-07: "mark all read doesn't do anything".)
    //
    // 2026-02-27: after backend flips read=true, WIPE the visible list
    // instead of leaving the greyed-out rows behind. The old notif
    // records still live on the server for audit, but the panel becomes
    // an "unread inbox" — once you mark them read, they're gone from
    // your view. User ask: "when you click mark all read it's only
    // clearing them, not removing the old ones".
    try {
      const qs = new URLSearchParams({ crew_id: user.crew_id, is_manager: String(isMgr) }).toString();
      await axios.put(`${API}/notifications/read-all?${qs}`);
      setNotifications([]);
    } catch (e) {
      console.error("Failed to mark all read:", e);
    }
  };

  // ---- Derive live stats from `/api/tally/{today}` — same source of
  // truth as CrewTallyPage's Store Progress card so the Right Now
  // scoreboard doesn't disagree with it. `total_emails` /
  // `store_transactions` / `capture_rate` are pre-computed server-side
  // from the latest checkpoint reading (2 PM if that's the most recent
  // one entered, 5 PM after that, etc.), so a fresh morning with no
  // transactions in yet correctly renders as "waiting" rather than
  // "0%".
  const captured = tally?.total_emails || 0;
  const myCaptures = tally?.crew_counts?.[user?.crew_id] || tally?.my_count || 0;
  const transactions = tally?.store_transactions || 0;
  const paceAmount = biz?.actual_sales || 0;
  const planAmount = biz?.plan_sales || 0;
  const pacePct = planAmount ? Math.round((paceAmount / planAmount) * 100) : 0;
  // Capture rate as a percentage (server returns a 0-1 float).
  const captureRate = tally?.capture_rate != null
    ? Math.round(Number(tally.capture_rate) * 1000) / 10
    : 0;
  // Real "vs yesterday" — computes yesterday's same-time capture rate
  // from `yTally` so the tile stops lying with a hardcoded `+8`.
  // `null` means we couldn't compute (yesterday store closed / no data
  // yet) — the UI hides the chip in that case.
  const yesterdayCaptureRate = (() => {
    if (yTally?.capture_rate == null && !yTally?.total_emails) return null;
    if (yTally?.capture_rate != null) {
      return Math.round(Number(yTally.capture_rate) * 1000) / 10;
    }
    const yEmails = Number(yTally?.total_emails || 0);
    const yTxn = Number(yTally?.store_transactions || 0);
    if (!yTxn) return null;
    return Math.round((yEmails / yTxn) * 1000) / 10;
  })();
  const captureDelta = (yesterdayCaptureRate == null)
    ? null
    : Math.round((captureRate - yesterdayCaptureRate) * 10) / 10;
  // "On floor" count — number of crew whose scheduled shift on today's
  // zone sheet includes the current time. Previously derived from
  // `tally.crew_counts` (people who had captured ≥1 email today), which
  // made a fresh morning always render "0 wow crew" even with a full
  // team scheduled. (User report 2026-02-26: "the who's on the floor
  // should be who's actually on the floor and not who's collecting
  // email".) Parses free-form shift strings like "9AM-530PM",
  // "10a-3p", "1230PM-530PM" into minute ranges and checks if `now`
  // falls inside. Rows with an unparseable / empty shift default to
  // "on floor" (better a slight over-count than hiding real bodies).
  const wowCrewCount = useMemo(() => {
    const rows = (sheet && sheet.crew_assignments) || [];
    if (!rows.length) return 0;
    const nowMST = new Date(now.toLocaleString("en-US", { timeZone: "America/Denver" }));
    const nowMin = nowMST.getHours() * 60 + nowMST.getMinutes();
    // Parse one side of a shift range ("9AM", "530PM", "1230p", etc.)
    // into minutes-since-midnight.
    const parseTime = (raw) => {
      if (!raw) return null;
      const s = String(raw).trim().toLowerCase().replace(/\s+/g, "");
      const m = s.match(/^(\d{1,2})(?::?(\d{2}))?(a|p|am|pm)?$/);
      if (!m) return null;
      let h = parseInt(m[1], 10);
      const min = m[2] ? parseInt(m[2], 10) : 0;
      const ap = m[3] || "";
      // If no am/pm marker but the number is 3-4 digits like "530",
      // assume the last two are minutes and we already extracted them.
      // Convert 12h → 24h using the am/pm hint; if absent, assume:
      //   hh < 8  → PM (opening manager's shift can start at 8am, but
      //             "5" almost always means 5pm not 5am for retail)
      //   hh >= 8 → AM
      if (ap.startsWith("p") && h < 12) h += 12;
      else if (ap.startsWith("a") && h === 12) h = 0;
      else if (!ap && h < 8) h += 12;
      return h * 60 + min;
    };
    const parseRange = (shift) => {
      if (!shift) return null;
      const parts = String(shift).split(/[-–—]|\bto\b/i).map((p) => p.trim()).filter(Boolean);
      if (parts.length < 2) return null;
      const start = parseTime(parts[0]);
      const end = parseTime(parts[1]);
      if (start == null || end == null) return null;
      return [start, end];
    };
    let count = 0;
    for (const r of rows) {
      const range = parseRange(r.shift);
      // No/bad shift → SKIP (previously we counted them as on-floor,
      // but that surfaced people who weren't scheduled yet or whose
      // row was still being edited. Better to under-count than to
      // lie about who's actually here — user report 2026-02-26).
      if (!range) continue;
      const [s, e] = range;
      if (nowMin >= s && nowMin <= e) count += 1;
    }
    return count;
  }, [sheet, now]);
  // Same shift-window filter, but returns which bucket each row falls
  // into so the Game Plan dialog can group by "On floor now / Starting
  // soon / Off shift" instead of dumping every scheduled row into one
  // list (which is what made the user say "on the floor is still
  // counting people who aren't here or already left"). Uses the same
  // parser as wowCrewCount so the count in the header stays honest.
  const floorRowsByBucket = useMemo(() => {
    const rows = (sheet && sheet.crew_assignments) || [];
    if (!rows.length) return { on: [], soon: [], off: [] };
    const nowMST = new Date(now.toLocaleString("en-US", { timeZone: "America/Denver" }));
    const nowMin = nowMST.getHours() * 60 + nowMST.getMinutes();
    const parseTime = (raw) => {
      if (!raw) return null;
      const s = String(raw).trim().toLowerCase().replace(/\s+/g, "");
      const m = s.match(/^(\d{1,2})(?::?(\d{2}))?(a|p|am|pm)?$/);
      if (!m) return null;
      let h = parseInt(m[1], 10);
      const min = m[2] ? parseInt(m[2], 10) : 0;
      const ap = m[3] || "";
      if (ap.startsWith("p") && h < 12) h += 12;
      else if (ap.startsWith("a") && h === 12) h = 0;
      else if (!ap && h < 8) h += 12;
      return h * 60 + min;
    };
    const parseRange = (shift) => {
      if (!shift) return null;
      const parts = String(shift).split(/[-–—]|\bto\b/i).map((p) => p.trim()).filter(Boolean);
      if (parts.length < 2) return null;
      const start = parseTime(parts[0]);
      const end = parseTime(parts[1]);
      if (start == null || end == null) return null;
      return [start, end];
    };
    const on = [], soon = [], off = [];
    for (const r of rows) {
      const range = parseRange(r.shift);
      if (!range) { off.push(r); continue; }
      const [s, e] = range;
      if (nowMin >= s && nowMin <= e) on.push(r);
      else if (nowMin < s) soon.push(r);
      else off.push(r);
    }
    return { on, soon, off };
  }, [sheet, now]);
  // Next checkpoint / close deadline. Uses live store hours from
  // /api/settings when available (respects per-day + per-store
  // overrides). Falls back to 14/17/20/21 defaults if settings
  // haven't loaded yet.
  const nextEvent = (() => {
    const nowMST = new Date(now.toLocaleString("en-US", { timeZone: "America/Denver" }));
    const cur = nowMST.getHours() * 60 + nowMST.getMinutes();
    const parseHM = (hm, fallback) => {
      const s = (hm || fallback || "").split(":");
      const h = Number(s[0] || 0);
      const m = Number(s[1] || 0);
      return h * 60 + m;
    };
    const events = [
      { label: "2 PM checkpoint", at: parseHM(effHours?.cp1_time, "14:00") },
      { label: "5 PM checkpoint", at: parseHM(effHours?.cp2_time, "17:00") },
      { label: "8 PM checkpoint", at: parseHM(effHours?.cp3_time, "20:00") },
      { label: "Store close",     at: parseHM(effHours?.close_time, "21:00") },
    ];
    for (const ev of events) {
      if (ev.at > cur) return { label: ev.label, min: ev.at - cur };
    }
    return { label: "Store closed", min: 0 };
  })();
  const alerts = isCrew
    ? []
    : isDM
      ? (dmUnread > 0
          ? [{ id: "dm-inbox", label: `${dmUnread} unread alert${dmUnread === 1 ? "" : "s"} in your bell`, route: "notifications" }]
          : dmRollup?.alerts_count
            ? [{ id: "cp-dm", label: `${dmRollup.alerts_count} store${dmRollup.alerts_count === 1 ? "" : "s"} missing checkpoint readings`, route: "district" }]
            : [])
      : [];
  // Managers' checkpoint countdown lives in the "Right Now → Next"
  // tile of the scoreboard below. We used to also render it as a red
  // banner at the top of Home, but that meant the SAME "2 PM checkpoint
  // in 834m" line was on screen twice at once. (User report 2026-02-26:
  // "what the heck? I'm not a fan of these double notifications its
  // an over kill".) The banner is now reserved for DM-level surfaces
  // (unread alerts, stores missing readings) and stays empty for
  // regular managers — the scoreboard is the single source of truth.
  // Real "last {weekday}" comparator string — reads yesterday's tally
  // + biz to compute the actual capture rate at the same weekday name.
  // Returns `""` when we can't compute (no data yesterday) so the UI
  // hides the whole line rather than fabricating one. (User report
  // 2026-02-26: "email capture is wrong" — the hardcoded "68% by 3 PM"
  // was a leftover mock and made the tile untrustworthy.)
  const yesterdayComp = (() => {
    if (yesterdayCaptureRate == null) return "";
    const yDayName = new Date(Date.now() - 24 * 60 * 60 * 1000)
      .toLocaleDateString("en-US", { weekday: "long" });
    return `Yesterday (${yDayName}) closed at ${yesterdayCaptureRate}%`;
  })();

  // ---- Live subtitles for the hero tiles (user ask 2026-08-07:
  // "swap the placeholder subtitles for real progress"). Each string
  // reads like a mini scoreboard so the tiles double as at-a-glance
  // status. Falls back to friendly text when the underlying data
  // isn't loaded yet.
  const fmtShortMoney = (n) => {
    const v = Number(n || 0);
    if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
    return `$${Math.round(v)}`;
  };
  const collectSubtitle = isCrew
    ? myCaptures ? `${myCaptures} today` : "Tap to add"
    : transactions > 0
      ? `${captured}/${transactions} · ${captureRate}%`
      : captured > 0
        ? `${captured} captured`
        : "Tap to add";
  const zoneSheetSubtitle = tasksSummary.total > 0
    ? `${tasksSummary.done}/${tasksSummary.total} tasks`
    : "Today’s plan";
  const mgrDashSubtitle = planAmount > 0
    ? `${fmtShortMoney(paceAmount)} / ${fmtShortMoney(planAmount)}`
    : paceAmount > 0
      ? `${fmtShortMoney(paceAmount)} pace`
      : "Win the day";

  // Contest promotion — when the admin has flipped `tile_visible` on
  // and we're inside the contest window, Contest jumps out of the
  // pill row and becomes a 4th hero tile. Otherwise it hides entirely.
  //
  // (Bug 2026-09-01: this used to be `const showContestHero = true`
  // — hard-coded ON to force the 2×2 hero grid — but that ignored the
  // admin's tile_visible toggle so a store manager couldn't hide the
  // tile after the contest ended. User: "email contest is over, so I
  // shut it off and it's still on the home screen". Now honours the
  // backend flag; when contests are off, the hero grid falls back to
  // the 3-tile row that predated the May 2026 contest.)
  const showContestHero = !!(contestState && contestState.tile_visible);
  const contestSubtitle = (() => {
    if (!contestState) return "Earn tickets";
    const tickets = Number(contestState.total_tickets || 0);
    const daysLeft = Number(contestState.days_left || 0);
    if (tickets > 0 && daysLeft > 0) return `${tickets} tickets · ${daysLeft}d left`;
    if (tickets > 0) return `${tickets} tickets earned`;
    if (daysLeft > 0) return `${daysLeft} days left`;
    return "Earn tickets";
  })();

  const nav = (route, meta) => { if (onNavigate) onNavigate(route, meta); };
  // Slide-up router — every "More pages" pill that has a full-page
  // component (Team / Leaderboard / Calendar / Contest) opens as a
  // bottom sheet over Home. Falls back to `nav()` when the parent
  // didn't wire the callback so this component still works standalone.
  const openSheet = (pageKey) => {
    if (onOpenSheet) { onOpenSheet(pageKey); return; }
    nav(pageKey);
  };
  const [showReportIssue, setShowReportIssue] = useState(false);

  // Notification row click — full port of the V1 handler so every bell
  // item routes to the right place. Types the switch supports:
  //   • eod_ready → open EODReportModal in-place
  //   • coverage_completion/handoff → Mgr Dash w/ preselect via sessionStorage
  //   • coverage_help_request → confirm + POST accept_help
  //   • coverage_help_accepted / coverage_nudge / story_* → home
  //   • checkpoint_reminder / eod_missing → Zone Sheet w/ slot preselect
  //   • bug_report → admin (BugReports tab); bug_report_self/status → inbox
  //   • store_closure → settings
  //   • lp_incident_new / lp_draft_reminder → Loss Prevention (id stashed)
  //   • fallthrough → inbox
  const handleNotificationClick = (n) => {
    // Single shared router (lib/notificationRouter.js) owns the full
    // type→destination map; both home surfaces delegate here so routing
    // can never drift. (User ask 2026-10-04: "we should only have 1 handler".)
    routeNotification(n, {
      nav,
      openSheet,
      openNotifications: () => setShowNotifications(true),
      setEodReport,
      crewId: user?.crew_id || "",
      closePanel: () => setShowNotifications(false),
    });
  };
  // Inline sheet state — Passcode, Tasks, Penny Rounding, Story composer
  // all open as slide-up sheets over Home instead of navigating away.
  // Matches V1's home behavior + user's V2 ask ("everything a slide up").
  const [tasksSheetOpen, setTasksSheetOpen] = useState(false);
  const [passcodeSheetOpen, setPasscodeSheetOpen] = useState(false);
  const [storyComposerOpen, setStoryComposerOpen] = useState(false);
  const [dmAlertsOpen, setDmAlertsOpen] = useState(false);
  const [eodReport, setEodReport] = useState(null);

  // Quiet usage tracking for the inline Home sheets (2026-10-01).
  useEffect(() => {
    const meta = { crewId: user?.crew_id, storeId: user?.home_store_id || user?.store_id || "", role: user?.role || "" };
    if (tasksSheetOpen) trackView("tasks", meta);
    if (passcodeSheetOpen) trackView("passcode", meta);
    if (storyComposerOpen) trackView("story-composer", meta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksSheetOpen, passcodeSheetOpen, storyComposerOpen]);


  // Derive avatar photo from the crew roster (App.js keeps it fresh via
  // the /api/crew/avatars merge). Falls back to the initial-circle
  // rendered by ProfileMenuButton when no photo is available.
  const myPhoto = (() => {
    const me = crew.find((c) => c.id === user?.crew_id);
    return me?.photo_url || user?.photo_url || "";
  })();
  const isAdmin = !!user?.is_admin;
  const canPostStory = !!(user?.is_manager || user?.is_admin || user?.is_district_manager || (user?.position && String(user.position).toLowerCase().includes("recovery captain")));

  return (
    // Background intentionally NOT set here — inherits `--background`
    // from body via index.css so all theme presets (Halloween, Midnight,
    // Spider-Man, Blueprint, etc.) show through instead of being masked
    // by a hardcoded slate-50. (User report 2026-02-26: "the background
    // of V2 doesn't align with the preset themes in settings".)
    <div className="min-h-screen" data-testid="home-v2-preview">
      {/* Notification enablement moved into the once-a-day DailyBriefing
          (App.js) 2026-06-21 — no more persistent top bar. PushHardGate
          retired. */}
      <WalkieFAB crewId={user?.crew_id || ""} />
      <div
        className="max-w-lg min-[541px]:max-[767px]:max-w-3xl md:max-w-5xl lg:max-w-6xl xl:max-w-6xl mx-auto px-4 pt-4 pb-0 space-y-4"
        style={{
          paddingTop: "max(1rem, env(safe-area-inset-top))",
          paddingLeft: "max(1rem, env(safe-area-inset-left))",
          paddingRight: "max(1rem, env(safe-area-inset-right))",
          paddingBottom: 0,
        }}
      >
        {/* "Finish setting up" nudge — notifications-off / no-photo. Shows
            for anyone who still needs either; self-hides once both are done.
            (User ask 2026-06: new hires weren't told to enable notifications;
            crew keep skipping photos.) */}
        <SetupNudgeCard
          crewId={user?.crew_id || ""}
          hasPhoto={!!myPhoto}
          onAddPhoto={() => nav("profile")}
        />
        {/* All-day birthday / anniversary banner — one tap to the card wall.
            (User ask 2026-06-21.) */}
        {birthday && (
          <button
            type="button"
            onClick={() => onOpenBirthday?.()}
            data-testid="home-birthday-banner"
            className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-left text-white shadow-lg active:scale-[0.99] transition-transform"
            style={{ background: "linear-gradient(135deg,#7c3aed,#db2777)" }}
          >
            <span className="text-2xl flex-none">{birthday.kind === "anniversary" ? "🏆" : "🎂"}</span>
            <span className="flex-1 min-w-0">
              <span className="block text-[15px] font-black leading-tight">
                {birthday.is_honoree
                  ? (birthday.kind === "anniversary" ? `It's your ${birthday.years}-year anniversary!` : "It's your birthday!")
                  : (birthday.kind === "anniversary"
                      ? `${birthday.first_name}'s ${birthday.years}-year anniversary`
                      : `It's ${birthday.first_name}'s Birthday!`)}
              </span>
              <span className="block text-[12px] font-semibold text-white/85">
                {birthday.is_honoree ? "Open your card →" : "Sign the card →"}
              </span>
            </span>
            {typeof birthday.signature_count === "number" && birthday.signature_count > 0 && (
              <span className="flex-none text-[11px] font-black bg-white/20 rounded-full px-2.5 py-1">🎈 {birthday.signature_count}</span>
            )}
          </button>
        )}
        {/* "You missed a celebration" belated strip — recent birthdays /
            anniversaries the viewer hasn't signed yet. Tapping opens the card
            so they can add a belated note; it drops off once they sign.
            (User ask 2026-06-21.) */}
        {belatedCards.length > 0 && (
          <div className="space-y-2" data-testid="home-belated-strip">
            <p className="text-[11px] font-black uppercase tracking-wider px-1" style={{ color: "var(--on-brand-text, #64748b)" }}>
              🎈 You missed a celebration
            </p>
            {belatedCards.map((c) => (
              <button
                key={`${c.honoree_id}-${c.kind}-${c.date}`}
                type="button"
                onClick={() => onOpenBelated?.(c)}
                data-testid={`home-belated-${c.honoree_id}`}
                className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-left bg-white shadow-sm active:scale-[0.99] transition-transform"
                style={{ border: "1px solid rgba(219,39,119,0.25)" }}
              >
                <span className="w-10 h-10 rounded-full flex-none flex items-center justify-center text-xl overflow-hidden" style={{ background: "linear-gradient(135deg,#f5d0fe,#fbcfe8)" }}>
                  {c.avatar_url ? (
                    <img src={c.avatar_url.startsWith("http") ? c.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${c.avatar_url}`} alt={c.first_name} className="w-full h-full object-cover" />
                  ) : (
                    <span>{c.kind === "anniversary" ? "🏆" : "🎂"}</span>
                  )}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-black leading-tight text-slate-900">
                    {c.kind === "anniversary"
                      ? `${c.first_name}'s ${c.years}-year anniversary`
                      : `${c.first_name}'s birthday`}
                  </span>
                  <span className="block text-[12px] font-semibold text-pink-600">
                    Send a belated note 🎈 →
                  </span>
                </span>
                <span className="flex-none text-[11px] font-bold text-slate-400">
                  {c.days_ago === 1 ? "yesterday" : `${c.days_ago}d ago`}
                </span>
              </button>
            ))}
          </div>
        )}
        {/* ═══════════════════ CONDENSED HEADER ═══════════════════ */}
        <header className="flex items-center gap-3" data-testid="home-v2-header">
          <div className="flex-1 min-w-0">
            {/* Greeting sits DIRECTLY on the theme body (no white card
                underneath) so we use `--on-brand-text` — the theme's
                literal light-text color on dark presets (Halloween
                yellow, Midnight slate, Spider-Man white), and safe
                slate-900 on light presets. */}
            <p className="text-[10px] font-black uppercase tracking-widest leading-tight opacity-70" style={{ color: "var(--on-brand-text, #94a3b8)" }}>
              {greetingText()}
            </p>
            <p className="text-lg font-black leading-tight truncate" style={{ color: "var(--on-brand-text, #0f172a)" }}>
              {user?.first_name || "Manager"}
            </p>
          </div>
          {/* Header cluster — matches V1: Inbox · Coupon · Bell · Sound
              · Avatar. Every action is inline; the avatar's slide-out
              menu still holds Post story / Edit profile / Settings /
              Admin / Reports / LP / Sign out. */}
          <div
            className="flex items-center gap-0.5 flex-shrink-0 bg-white rounded-full p-1 shadow-sm"
            style={{ border: "1px solid rgba(148,163,184,0.18)" }}
            data-testid="home-v2-header-cluster"
          >
            <button
              onClick={() => nav("inbox")}
              className="relative w-9 h-9 rounded-full hover:bg-emerald-50 transition-colors flex items-center justify-center"
              data-testid="home-v2-inbox-btn"
              aria-label={inboxUnread > 0 ? `${inboxUnread} unread messages` : "Inbox"}
              title="Inbox"
            >
              <ChatsCircle weight="bold" className="w-4 h-4 text-emerald-600" />
              {inboxUnread > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 text-[9px] font-black text-white min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center shadow tabular-nums"
                  style={{ backgroundColor: "#059669" }}
                  data-testid="home-v2-inbox-badge"
                >
                  {inboxUnread > 99 ? "99+" : inboxUnread}
                </span>
              )}
            </button>
            <CouponHeaderPill />
            <button
              onClick={() => setShowNotifications(true)}
              className={`relative w-9 h-9 rounded-full transition-all flex items-center justify-center ${unreadCount > 0 ? "bg-blue-100 ring-2 ring-blue-400" : "hover:bg-blue-50"}`}
              data-testid="home-v2-notifications-btn"
              aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications"}
              title="Notifications"
            >
              <Bell
                weight={unreadCount > 0 ? "fill" : "bold"}
                className="w-4 h-4"
                style={{ color: unreadCount > 0 ? "var(--fb-blue)" : "#64748B" }}
              />
              {unreadCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 text-[9px] font-black text-white min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center shadow"
                  style={{ backgroundColor: "var(--fb-blue)" }}
                  data-testid="home-v2-notifications-badge"
                >
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={toggleCueMuted}
              className="w-9 h-9 rounded-full hover:bg-slate-100 transition-colors flex items-center justify-center"
              data-testid="home-v2-sound-btn"
              aria-label={cueMuted ? "Unmute notification sound" : "Mute notification sound"}
              title={cueMuted ? "Sound muted — tap to unmute" : "Sound on — tap to mute"}
            >
              {cueMuted
                ? <SpeakerSlash weight="bold" className="w-4 h-4 text-slate-400" />
                : <SpeakerHigh weight="bold" className="w-4 h-4 text-slate-600" />}
            </button>
            <ProfileMenuButton
              user={user}
              myPhoto={myPhoto}
              storiesEnabled={canPostStory}
              onEditProfile={() => nav("profile")}
              onPostStory={() => setStoryComposerOpen(true)}
              onSettings={() => nav("settings")}
              onAdmin={() => nav("admin")}
              onOpenReports={(isManager || isDM) ? () => nav("reports") : undefined}
              onOpenKeepsake={onOpenKeepsake}
              onSignOut={onSwitchUser}
              isAdmin={isAdmin}
            />
          </div>
        </header>

        {/* Pitch Black event hero — Darkrai + live 09/12 countdown.
            Only renders while the Pitch Black theme is active. */}
        <PitchBlackHero active={isPitchBlack} onCta={() => nav("inbox")} />

        {/* ═══════════════════ STORIES — real StoriesRow so posting,
             viewing, and mentions all work identically to V1. Composer
             is controlled from here so it can be triggered from the
             Profile menu ("Post a story") too. ═══════════════════ */}
        <section data-testid="home-v2-stories">
          <div className="flex items-center justify-between px-1 mb-1">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] opacity-60" style={{ color: "var(--on-brand-text, #94a3b8)" }}>
              Today’s stories
            </p>
          </div>
          <StoriesRow
            user={user}
            isManager={isManager || isDM}
            enabled
            composerOpen={storyComposerOpen}
            onComposerOpenChange={setStoryComposerOpen}
          />
        </section>

        {/* ═══════════════════ ALERTS BANNER ═══════════════════ */}
        {alerts.length > 0 && (
          <button
            onClick={() => {
              // DMs get a drill-down sheet; managers still deep-link to
              // the zone sheet (single-store context).
              if (isDM) { setDmAlertsOpen(true); return; }
              if (alerts[0].route === "notifications") { setShowNotifications(true); return; }
              nav(alerts[0].route);
            }}
            className="w-full flex items-center gap-3 bg-rose-50 border-2 border-rose-200 rounded-xl px-4 py-3 text-left hover:bg-rose-100 transition-colors"
            data-testid="home-v2-alerts"
          >
            <div className="w-9 h-9 rounded-full bg-rose-500 text-white flex items-center justify-center flex-shrink-0">
              <Warning weight="fill" className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-wider text-rose-700">
                {alerts.length} thing needs you
              </p>
              <p className="text-sm font-black text-rose-900 leading-tight mt-0.5 truncate">{alerts[0].label}</p>
            </div>
            <ArrowRight weight="bold" className="w-4 h-4 text-rose-500 flex-shrink-0" />
          </button>
        )}

        {/* ═══════════════════ "RIGHT NOW" HERO (managers only) ═══════════════════ */}
        {isManager && (
          <section
            className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-5 text-white overflow-hidden relative"
            style={{ boxShadow: "0 10px 40px -12px rgba(15,23,42,0.35)" }}
            data-testid="home-v2-hero"
          >
            <div
              className="absolute inset-0 opacity-[0.06] pointer-events-none"
              style={{
                backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
                backgroundSize: "20px 20px",
              }}
            />
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-white/60">Right now</p>
                <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/10 text-white/70">
                  {now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                </span>
              </div>

              <div className="grid grid-cols-2 min-[541px]:max-[767px]:grid-cols-4 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-wider text-emerald-300 flex items-center gap-1">
                    <TrendUp weight="fill" className="w-3 h-3" /> Capture
                  </p>
                  <p className="text-3xl font-black leading-none mt-1">{captureRate}%</p>
                  {captureDelta != null && (
                    <p className="text-[10px] text-white/60 mt-0.5" data-testid="right-now-capture-delta">
                      {captureDelta > 0 ? `+${captureDelta}` : captureDelta}% vs yesterday
                    </p>
                  )}
                  {captureDelta == null && (
                    <p className="text-[10px] text-white/40 mt-0.5">
                      {captured}/{transactions || "—"} today
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-wider text-amber-300 flex items-center gap-1">
                    <ChartLineUp weight="fill" className="w-3 h-3" /> Pace
                  </p>
                  <p className="text-3xl font-black leading-none mt-1">{fmtMoney(paceAmount)}</p>
                  <p className="text-[10px] text-white/60 mt-0.5">{pacePct}% of {fmtMoney(planAmount)} plan</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-wider text-sky-300 flex items-center gap-1">
                    <Clock weight="fill" className="w-3 h-3" /> Next
                  </p>
                  <p className="text-lg font-black leading-tight mt-1">{nextEvent.label}</p>
                  <p className="text-[10px] text-white/60 mt-0.5">in {nextEvent.min} min</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowFloorDialog(true)}
                  className="text-left rounded-lg -m-1 p-1 hover:bg-white/5 active:bg-white/10 transition-colors"
                  data-testid="right-now-on-floor-btn"
                >
                  <p className="text-[10px] font-black uppercase tracking-wider text-violet-300 flex items-center gap-1">
                    <Users weight="fill" className="w-3 h-3" /> On floor
                  </p>
                  <p className="text-lg font-black leading-tight mt-1">{wowCrewCount} wow crew</p>
                  <p className="text-[10px] text-white/60 mt-0.5">Tap for game plan</p>
                </button>
              </div>

              {yesterdayComp && (
                <div className="mt-4 pt-3 border-t border-white/10 flex items-center gap-2">
                  <Sparkle weight="fill" className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
                  <p className="text-[11px] text-white/80 leading-snug">
                    {yesterdayComp}
                    {captureDelta != null && captureDelta > 0 ? " — you’re ahead." : captureDelta != null && captureDelta < 0 ? " — push harder." : ""}
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ═══════════════════ DM DISTRICT ROLLUP HERO ═══════════════════ */}
        {isDM && (() => {
          // Real data when the DM live-rollup endpoint returned a payload;
          // fallback mocks so the preview still renders for non-DMs who
          // toggle into this tab.
          const d = dmRollup;
          const districtLabel = d?.district_id && d.district_id !== "all"
            ? d.district_id.replace(/^district_/, "").replace(/_/g, " ")
            : "District";
          const capturePct = d ? d.capture_pct : 54;
          const pace = d ? d.pace : 28400;
          const plan = d ? d.plan : 30800;
          const pacePctD = plan ? Math.round((pace / plan) * 100) : 0;
          const topStore = d?.top_store || { store_code: "3094", capture_pct: 78, manager_name: "Sarah" };
          const worstStore = d?.worst_store || { store_code: "8812", capture_pct: 31, manager_name: "Mike" };
          const storeCount = d?.store_count ?? 8;
          const dmSubtitle = d
            ? `${storeCount} stores tracked live`
            : "Preview data — sign in as DM for live rollup";
          return (
          <section
            className="bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 rounded-2xl p-5 text-white overflow-hidden relative"
            style={{ boxShadow: "0 10px 40px -12px rgba(49,46,129,0.45)" }}
            data-testid="home-v2-dm-hero"
          >
            <div
              className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{
                backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
                backgroundSize: "20px 20px",
              }}
            />
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-white/70 flex items-center gap-1.5">
                  <Crown weight="fill" className="w-3 h-3 text-amber-300" /> {districtLabel} · Right now
                </p>
                <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/10 text-white/70">
                  {now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-wider text-emerald-300 flex items-center gap-1">
                    <TrendUp weight="fill" className="w-3 h-3" /> District capture
                  </p>
                  <p className="text-3xl font-black leading-none mt-1">{capturePct}%</p>
                  <p className="text-[10px] text-white/60 mt-0.5">avg across {storeCount} stores</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-wider text-amber-300 flex items-center gap-1">
                    <ChartLineUp weight="fill" className="w-3 h-3" /> District pace
                  </p>
                  <p className="text-3xl font-black leading-none mt-1">{fmtMoney(pace)}</p>
                  <p className="text-[10px] text-white/60 mt-0.5">{pacePctD}% of {fmtMoney(plan)} plan</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-wider text-lime-300 flex items-center gap-1">
                    <CaretUp weight="fill" className="w-3 h-3" /> Top store
                  </p>
                  <p className="text-lg font-black leading-tight mt-1">Store {topStore.store_code}</p>
                  <p className="text-[10px] text-white/60 mt-0.5">{topStore.capture_pct}% capture{topStore.manager_name ? ` · ${topStore.manager_name}` : ""}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-wider text-rose-300 flex items-center gap-1">
                    <CaretDown weight="fill" className="w-3 h-3" /> Needs help
                  </p>
                  <p className="text-lg font-black leading-tight mt-1">
                    {worstStore ? `Store ${worstStore.store_code}` : "None yet"}
                  </p>
                  <p className="text-[10px] text-white/60 mt-0.5">
                    {worstStore ? `${worstStore.capture_pct}% capture${worstStore.manager_name ? ` · ${worstStore.manager_name}` : ""}` : "No stores open"}
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-white/10 flex items-center gap-2">
                <Sparkle weight="fill" className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
                <p className="text-[11px] text-white/80 leading-snug">{dmSubtitle}</p>
              </div>
            </div>
          </section>
          );
        })()}

        {/* CREW personal capture hero removed 2026-06 — the "Tap to add /
            Collect Emails" tile below already covers this, so the big card
            was redundant and just ate vertical space. */}

        {/* ═══════════════════ TASKS + HIGH FIVES ═══════════════════
             High Fives promoted to this prominent top slot 2026-10-04
             (user ask: "move high five up to daily passcode… encourage
             high fives"). Daily Passcode moved below the hero actions. */}
        <section className="grid grid-cols-2 gap-3" data-testid="home-v2-tasks-passcode">
          <TasksTile
            done={tasksSummary.done}
            total={tasksSummary.total}
            inProgress={tasksSummary.inProgress}
            onClick={() => setTasksSheetOpen(true)}
          />
          <HighFiveTile onClick={() => nav("recognition")} />
        </section>

        {/* ═══════════════════ HERO ACTIONS (per-role top 3, or 4 when
             the seasonal contest is featured) ═══════════════════ */}
        <section className="space-y-2">
          <p className="text-[10px] font-black uppercase tracking-[0.22em] opacity-60 px-1" style={{ color: "var(--on-brand-text, #94a3b8)" }}>Jump to</p>
          <div className={showContestHero ? "grid grid-cols-2 md:grid-cols-4 gap-3" : "grid grid-cols-3 gap-3"}>
            {isDM ? (
              <>
                <HeroTile
                  icon={Buildings}
                  title="District"
                  subtitle={dmRollup?.store_count ? `${dmRollup.store_count} stores` : "District"}
                  gradient={TILE_GRADIENTS.district}
                  glow="rgba(99,102,241,0.65)"
                  onClick={() => nav("district")}
                  testid="home-v2-hero-district"
                />
                <HeroTile
                  icon={Broadcast}
                  title="Store Mirror"
                  subtitle="Jump in"
                  gradient={TILE_GRADIENTS.mirror}
                  glow="rgba(16,185,129,0.6)"
                  onClick={() => nav("admin")}
                  testid="home-v2-hero-mirror"
                />
                <HeroTile
                  icon={ChartLineUp}
                  title="Mgr Dash"
                  subtitle={mgrDashSubtitle}
                  gradient={TILE_GRADIENTS.mgr}
                  glow="rgba(217,70,239,0.6)"
                  onClick={() => nav("manager")}
                  testid="home-v2-hero-mgr-dash"
                />
                {showContestHero && (
                  <HeroTile
                    icon={Confetti}
                    title="Contest"
                    subtitle={contestSubtitle}
                    gradient={TILE_GRADIENTS.contest}
                    glow="rgba(251,191,36,0.7)"
                    onClick={() => openSheet("contest")}
                    testid="home-v2-hero-contest"
                  />
                )}
              </>
            ) : isCrew ? (
              <>
                <HeroTile
                  icon={EnvelopeSimple}
                  title="Collect Emails"
                  subtitle={collectSubtitle}
                  gradient={TILE_GRADIENTS.collect}
                  glow="rgba(59,130,246,0.7)"
                  onClick={() => nav("tally")}
                  testid="home-v2-hero-collect"
                />
                <HeroTile
                  icon={ClipboardText}
                  title="Zone Sheet"
                  subtitle={zoneSheetSubtitle}
                  gradient={TILE_GRADIENTS.zone}
                  glow="rgba(251,146,60,0.65)"
                  onClick={() => nav("zone-sheet")}
                  testid="home-v2-hero-zone-sheet"
                />
                <HeroTile
                  icon={Trophy}
                  title="Leaderboards"
                  subtitle="Your rank"
                  gradient={TILE_GRADIENTS.leaders}
                  glow="rgba(250,204,21,0.65)"
                  onClick={() => openSheet("leaderboard")}
                  testid="home-v2-hero-leaderboards"
                />
                {showContestHero && (
                  <HeroTile
                    icon={Confetti}
                    title="Contest"
                    subtitle={contestSubtitle}
                    gradient={TILE_GRADIENTS.contest}
                    glow="rgba(251,191,36,0.7)"
                    onClick={() => openSheet("contest")}
                    testid="home-v2-hero-contest"
                  />
                )}
              </>
            ) : (
              <>
                <HeroTile
                  icon={EnvelopeSimple}
                  title="Collect Emails"
                  subtitle={collectSubtitle}
                  gradient={TILE_GRADIENTS.collect}
                  glow="rgba(59,130,246,0.7)"
                  onClick={() => nav("tally")}
                  testid="home-v2-hero-collect"
                />
                <HeroTile
                  icon={ClipboardText}
                  title="Zone Sheet"
                  subtitle={zoneSheetSubtitle}
                  gradient={TILE_GRADIENTS.zone}
                  glow="rgba(251,146,60,0.65)"
                  onClick={() => nav("zone-sheet")}
                  testid="home-v2-hero-zone-sheet"
                />
                <HeroTile
                  icon={ChartLineUp}
                  title="Mgr Dash"
                  subtitle={mgrDashSubtitle}
                  gradient={TILE_GRADIENTS.mgr}
                  glow="rgba(217,70,239,0.6)"
                  onClick={() => nav("manager")}
                  testid="home-v2-hero-mgr-dash"
                />
                {showContestHero && (
                  <HeroTile
                    icon={Confetti}
                    title="Contest"
                    subtitle={contestSubtitle}
                    gradient={TILE_GRADIENTS.contest}
                    glow="rgba(251,191,36,0.7)"
                    onClick={() => openSheet("contest")}
                    testid="home-v2-hero-contest"
                  />
                )}
              </>
            )}
          </div>
        </section>

        {/* ═══════════════════ PAGE PILLS (per-role, 2-col grid) ═══════════════════ */}
        <section data-testid="home-v2-chips">
          <p className="text-[10px] font-black uppercase tracking-[0.22em] opacity-60 px-1 mb-2" style={{ color: "var(--on-brand-text, #94a3b8)" }}>More pages</p>
          <div className="grid grid-cols-2 min-[541px]:max-[767px]:grid-cols-4 md:grid-cols-3 xl:grid-cols-4 gap-2">
            {/* Daily Passcode — demoted to a pill the SAME size as the other
                More pages pills (2026-10-04 user ask). High Fives now owns
                the prominent top tile slot. Emerald tint when today's code
                is ready to show. */}
            <PagePill
              icon={LockKey}
              label="Daily Passcode"
              count={null}
              tint={passcodeReady ? "emerald" : "slate"}
              onClick={() => setPasscodeSheetOpen(true)}
            />
            {isDM && (
              <>
                <PagePill icon={Storefront}   label="All Stores"    count={8}              tint="violet"  onClick={() => nav("district")} />
                <PagePill icon={Broadcast}    label="Mirror"        count={null}           tint="emerald" onClick={() => nav("admin")} />
                <PagePill icon={Trophy}       label="District LB"   count={null}           tint="amber"   onClick={() => openSheet("leaderboard")} />
                <PagePill icon={Users}        label="Visiting Crew" count={3}              tint="sky"     onClick={() => openSheet("team")} />
                <PagePill icon={CalendarDots} label="What’s Up"     count={2}              tint="slate"   onClick={() => openSheet("calendar")} />
              </>
            )}
            {isManager && (
              <>
                <PagePill icon={Users}        label="Wow Crew"     count={wowCrewCount}   tint="violet"  onClick={() => openSheet("team")} />
                <PagePill icon={Trophy}       label="Leaderboards" count={null}           tint="amber"   onClick={() => openSheet("leaderboard")} />
                <PagePill icon={CalendarDots} label="What’s Up"    count={2}              tint="sky"     onClick={() => openSheet("calendar")} />
                <PagePill icon={Buildings}    label="District"     count={null}           tint="slate"   onClick={() => nav("district")} />
              </>
            )}
            {isCrew && (
              <>
                <PagePill icon={Users}        label="Wow Crew"     count={wowCrewCount}   tint="violet"  onClick={() => openSheet("team")} />
                <PagePill icon={Trophy}       label="Leaderboards" count={null}           tint="amber"   onClick={() => openSheet("leaderboard")} />
                <PagePill icon={CalendarDots} label="What’s Up"    count={2}              tint="sky"     onClick={() => openSheet("calendar")} />
              </>
            )}
          </div>
        </section>

        {/* ═══════════════════ FOOTER (compact overflow) ═══════════════════ */}
        <footer className="pt-4 flex items-center justify-center gap-3 text-[10px] flex-wrap opacity-60" style={{ color: "var(--on-brand-text, #94a3b8)" }}>
          <InstallAppPrompt variant="footer" />
          {formatLastLogin(user?.previous_login) && (
            <>
              <span>·</span>
              <span data-testid="home-v2-last-login">
                {formatLastLogin(user.previous_login)}
              </span>
            </>
          )}
          <span>·</span>
          <button
            onClick={() => setShowReportIssue(true)}
            className="hover:text-slate-700 inline-flex items-center gap-1"
            data-testid="home-v2-feedback-btn"
          >
            <ChatCircleDots weight="bold" className="w-3 h-3" /> Feedback
          </button>
          <span>·</span>
          <span data-testid="home-v2-version">v{pkg.version}</span>
        </footer>
      </div>

      {/* Notifications panel — mounted at the root so it overlays every
          section. Fed by the periodic /api/notifications poll above. */}
      {showNotifications && (
        <NotificationsPanel
          notifications={notifications}
          onClose={() => setShowNotifications(false)}
          onMarkAllRead={markAllRead}
          onRowClick={handleNotificationClick}
          appVersion={pkg.version}
          currentUser={user}
          onRefresh={() => { /* poll handles it; noop keeps the panel happy */ }}
        />
      )}

      {/* Game Plan dialog — "who's on the floor + tap for tasks/ack".
          Opens from the Right Now scoreboard's On Floor tile. Reads
          from `sheet.crew_assignments` (already fetched at page load)
          so no extra network hit on open. (User ask 2026-02-26.) */}
      <Dialog open={showFloorDialog} onOpenChange={(o) => { if (!o) { setShowFloorDialog(false); setFloorExpandedCrewId(""); } }}>
        <DialogContent
          className="p-0 gap-0 max-w-md w-[calc(100%-16px)] max-h-[85vh] overflow-hidden rounded-3xl"
          data-testid="floor-game-plan-dialog"
        >
          <DialogTitle className="sr-only">Who is on the floor — game plan</DialogTitle>
          <div className="px-5 pt-5 pb-3 border-b border-slate-200 bg-slate-50">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Game Plan</p>
            <p className="text-xl font-black text-slate-900 mt-0.5">On the floor · {wowCrewCount}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">Tap a name to see their tasks + progress.</p>
          </div>
          <div className="overflow-y-auto flex-1 max-h-[70vh]">
            {(() => {
              const { on, soon, off } = floorRowsByBucket;
              const totalRows = on.length + soon.length + off.length;
              if (!totalRows) {
                return (
                  <div className="px-5 py-8 text-center">
                    <p className="text-sm text-slate-500">No one on the zone sheet yet today.</p>
                  </div>
                );
              }
              // Row renderer shared by all three buckets so a single
              // change (e.g., new status chip) lands everywhere.
              const renderRow = (r) => {
                const tasks = Array.isArray(r.coverage_tasks) ? r.coverage_tasks : [];
                const done = tasks.filter((t) => t.status === "completed" || t.status === "manager_verified").length;
                const ack = tasks.filter((t) => t.status !== "not_acknowledged").length;
                const expanded = floorExpandedCrewId === r.crew_id;
                const summary = tasks.length
                  ? `${done}/${tasks.length} done · ${ack} acknowledged`
                  : "No coverage tasks";
                return (
                  <div key={r.crew_id} className="border-b border-slate-100 last:border-b-0" data-testid={`floor-row-${r.crew_id}`}>
                    <div className="w-full px-5 py-3 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => setFloorExpandedCrewId(expanded ? "" : r.crew_id)}
                        className="text-left flex-1 min-w-0 hover:opacity-80"
                        data-testid={`floor-row-${r.crew_id}-expand`}
                      >
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-black text-slate-900 truncate">{r.crew_name || "Crew"}</p>
                          {r.is_visitor && r.home_store_code && (
                            <span className="text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 flex-shrink-0">
                              Visiting {r.home_store_code}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 truncate mt-0.5">
                          {r.shift ? `${r.shift} · ` : ""}{summary}
                        </p>
                      </button>
                      {(isManager || user?.is_admin || isDM) && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setReportCardCrewId(r.crew_id); }}
                          className="text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 flex-shrink-0"
                          data-testid={`floor-row-${r.crew_id}-report-card`}
                        >
                          Report card
                        </button>
                      )}
                    </div>
                    {expanded && (
                      <div className="px-5 pb-3 -mt-1 space-y-1.5" data-testid={`floor-row-${r.crew_id}-tasks`}>
                        {tasks.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">No coverage tasks assigned.</p>
                        ) : tasks.map((t) => {
                          const isDone = t.status === "completed" || t.status === "manager_verified";
                          const isAck = t.status !== "not_acknowledged";
                          const chipClass = isDone
                            ? "bg-emerald-100 text-emerald-800"
                            : t.status === "needs_attention" || t.status === "needs_handoff"
                              ? "bg-rose-100 text-rose-800"
                              : isAck
                                ? "bg-amber-100 text-amber-800"
                                : "bg-slate-100 text-slate-600";
                          const chipLabel = ({
                            not_acknowledged: "New",
                            acknowledged: "Got it",
                            in_progress: "In progress",
                            completed: "Done",
                            manager_verified: "Verified",
                            needs_attention: "Needs attn",
                            needs_handoff: "Handoff",
                          }[t.status] || t.status);
                          return (
                            <div key={t.id} className="flex items-center justify-between gap-2 py-1">
                              <span className="text-[13px] font-semibold text-slate-800 truncate">{t.label}</span>
                              <span className={`text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded ${chipClass} flex-shrink-0`}>
                                {chipLabel}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              };
              // Section header rendered above each non-empty bucket so
              // the user can tell "on floor now" apart from "left
              // already" without reading shift strings (user report
              // 2026-02-26: "on the floor is still counting people
              // who aren't here or already left for the day").
              const section = (label, testid, tint, arr) => arr.length ? (
                <div key={testid}>
                  <div className={`px-5 py-2 text-[10px] font-black uppercase tracking-widest ${tint} bg-slate-50/70 border-b border-slate-100`} data-testid={testid}>
                    {label} · {arr.length}
                  </div>
                  {arr.map(renderRow)}
                </div>
              ) : null;
              return (
                <>
                  {on.length === 0 && (
                    <div className="px-5 py-4 text-center bg-amber-50/40 border-b border-amber-100">
                      <p className="text-sm font-bold text-slate-700">No one on the floor right now.</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">Next shift starts soon — check below.</p>
                    </div>
                  )}
                  {section("On floor now", "floor-bucket-on", "text-emerald-700", on)}
                  {section("Starting later today", "floor-bucket-soon", "text-sky-700", soon)}
                  {section("Off shift", "floor-bucket-off", "text-slate-500", off)}
                </>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>



      {/* Crew Report Card — reusable manager pop-out surfaced from
          "On the floor" rows on Home (2026-02-26). User ask: "make
          every team member clickable with their report card to see
          tasks, goals and in progress, messages". */}
      <CrewReportCardDialog
        open={!!reportCardCrewId}
        onOpenChange={(o) => { if (!o) setReportCardCrewId(""); }}
        crewId={reportCardCrewId}
        actorId={user?.crew_id || user?.id || ""}
        date={today}
      />

      {/* EOD Report modal — opened in-place when the user taps an
          `eod_ready` notification. Same modal V1 mounts. */}
      {eodReport && (
        <EODReportModal
          date={eodReport.date}
          storeId={eodReport.store_id}
          user={user}
          onClose={() => setEodReport(null)}
        />
      )}

      {/* Report an issue — same modal as V1. Bug/feature/question form
          fans out to admin inbox + email. */}
      {showReportIssue && (
        <ReportIssueModal
          user={user}
          currentPage="home-v2"
          onClose={() => setShowReportIssue(false)}
        />
      )}

      {/* Sheets for the Tasks + Passcode tiles — the components render
          only their internal <Sheet> when `hideSummary` is true, so we
          don't get duplicate cards on Home. */}
      <YourTasksTodayCard
        crewId={user?.crew_id}
        storeId={user?.home_store_id || ""}
        hideSummary
        open={tasksSheetOpen}
        onOpenChange={setTasksSheetOpen}
      />
      <DailyPasscodeCard
        user={user}
        hideBar
        open={passcodeSheetOpen}
        onOpenChange={setPasscodeSheetOpen}
        onClaimed={() => setPasscodeReady(true)}
      />

      {/* Penny Rounding retired 2026-06-21 — coupons disabled. */}

      {/* DM Alerts drill-down — lists the individual stores flagged in
          the district live-rollup so the DM can jump straight to the
          store that needs help instead of guessing from a summary. */}
      <Sheet open={dmAlertsOpen} onOpenChange={setDmAlertsOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto p-0" data-testid="home-v2-dm-alerts-sheet">
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
            <SheetTitle className="text-lg font-black flex items-center gap-2">
              <Warning weight="fill" className="w-5 h-5 text-rose-500" />
              District alerts
            </SheetTitle>
            <p className="text-xs text-slate-500 mt-1">
              {(dmRollup?.alerts || []).length > 0
                ? `${dmRollup.alerts.length} store${dmRollup.alerts.length === 1 ? "" : "s"} need attention`
                : "Everything looks good across your district."}
            </p>
          </SheetHeader>
          <div className="p-4 space-y-2">
            {(dmRollup?.alerts || []).map((s) => (
              <button
                key={s.store_id}
                onClick={() => { setDmAlertsOpen(false); nav("district"); }}
                className="w-full flex items-center gap-3 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-left hover:bg-rose-100 transition-colors"
                data-testid={`home-v2-dm-alert-${s.store_code}`}
              >
                <div className="w-10 h-10 rounded-full bg-rose-500 text-white flex items-center justify-center flex-shrink-0 font-black text-sm">
                  {s.store_code}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-black text-rose-900 leading-tight truncate">
                    {s.store_name || `Store ${s.store_code}`}
                  </p>
                  <p className="text-[11px] text-rose-700 mt-0.5 leading-tight">
                    {s.alert}{s.manager_name ? ` · ${s.manager_name}` : ""}
                  </p>
                </div>
                <ArrowRight weight="bold" className="w-4 h-4 text-rose-400 flex-shrink-0" />
              </button>
            ))}
            {(!dmRollup?.alerts || dmRollup.alerts.length === 0) && (
              <div className="text-center py-10 text-sm text-slate-500">
                <Sparkle weight="fill" className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                No open alerts across your district.
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── HERO tile ────────────────────────────────────────────────────────
// ─── HERO tile ────────────────────────────────────────────────────────
// Premium look — glass icon disc with a colored glow ring, large live
// metric, dot-grain texture for depth, and a subtle bottom sheen so the
// three tiles read as a "premium button" set rather than flat blocks.
// User ask 2026-08-07: "make the tiles + icons better — they are wide,
// colorful, and plain".
//
// `gradient` is now an inline CSS gradient string (e.g. "linear-gradient(
// 135deg, #2563eb 0%, #4338ca 55%, #0f172a 100%)") to sidestep the
// Tailwind JIT's arbitrary-combo miss that painted every tile the same
// warm shade during the first pass.
function HeroTile({ icon: Icon, title, subtitle, gradient, onClick, testid, glow = "rgba(255,255,255,0.4)" }) {
  return (
    <button
      onClick={onClick}
      className="group relative rounded-3xl overflow-hidden text-white active:scale-[0.96] transition-all min-h-[148px] flex flex-col justify-between p-4 hover:-translate-y-0.5 hover:shadow-xl"
      style={{ boxShadow: "0 8px 24px -8px rgba(15,23,42,0.35), inset 0 1px 0 rgba(255,255,255,0.15)", background: gradient }}
      data-testid={testid}
    >
      {/* Directional darken so text sits comfortably at the bottom */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" aria-hidden />
      {/* Dot-grain texture — 6% opacity, adds depth without a real image */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "14px 14px",
        }}
        aria-hidden
      />
      {/* Soft top-right corner glow — colored per tile via `glow` prop */}
      <div
        className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-2xl pointer-events-none"
        style={{ backgroundColor: glow, opacity: 0.45 }}
        aria-hidden
      />

      {/* Icon in a frosted-glass circle */}
      <div className="relative">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center relative"
          style={{
            background: "rgba(255,255,255,0.14)",
            backdropFilter: "blur(6px)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.35), 0 4px 12px -4px rgba(0,0,0,0.35)",
          }}
        >
          <Icon weight="fill" className="w-5 h-5" />
          {/* Icon glow halo */}
          <div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{ boxShadow: `0 0 24px ${glow}`, opacity: 0.55 }}
          />
        </div>
      </div>

      {/* Bottom — big metric + label */}
      <div className="relative">
        <p className="text-lg font-black leading-none tracking-tight">{subtitle}</p>
        <p className="text-[10px] font-black uppercase tracking-[0.16em] opacity-80 mt-1.5 leading-tight">
          {title}
        </p>
      </div>

      {/* Subtle bottom sheen line so the tile has a "button lip" */}
      <div
        className="absolute inset-x-4 bottom-0 h-px pointer-events-none"
        style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)" }}
        aria-hidden
      />
    </button>
  );
}

// Tile gradient presets — inline CSS so they render identically even if
// the Tailwind JIT hasn't built a specific arbitrary combo yet.
const TILE_GRADIENTS = {
  collect:  "linear-gradient(135deg, #2563eb 0%, #4338ca 55%, #0f172a 100%)", // blue → indigo → slate
  zone:     "linear-gradient(135deg, #d97706 0%, #b45309 55%, #0f172a 100%)", // amber → orange → slate
  mgr:      "linear-gradient(135deg, #a21caf 0%, #be185d 55%, #0f172a 100%)", // fuchsia → pink → slate
  district: "linear-gradient(135deg, #4338ca 0%, #312e81 55%, #0f172a 100%)", // indigo → deep-indigo → slate
  mirror:   "linear-gradient(135deg, #059669 0%, #115e59 55%, #0f172a 100%)", // emerald → teal → slate
  leaders:  "linear-gradient(135deg, #eab308 0%, #b45309 55%, #0f172a 100%)", // yellow → amber → slate
  contest:  "linear-gradient(135deg, #f59e0b 0%, #dc2626 55%, #0f172a 100%)", // amber → red → slate
};

// ─── PAGE PILL (style A refined) ──────────────────────────────────────
// Icon disc (colored) + label + optional numeric badge, in a rounded pill.
const PILL_TINTS = {
  violet:  { disc: "bg-violet-100 text-violet-600",   badge: "bg-violet-500 text-white" },
  amber:   { disc: "bg-amber-100 text-amber-700",     badge: "bg-amber-500 text-white" },
  sky:     { disc: "bg-sky-100 text-sky-600",         badge: "bg-sky-500 text-white" },
  slate:   { disc: "bg-slate-100 text-slate-600",     badge: "bg-slate-500 text-white" },
  emerald: { disc: "bg-emerald-100 text-emerald-700", badge: "bg-emerald-500 text-white" },
  rose:    { disc: "bg-rose-100 text-rose-600",       badge: "bg-rose-500 text-white" },
};
function PagePill({ icon: Icon, label, count, tint = "slate", onClick }) {
  const t = PILL_TINTS[tint] || PILL_TINTS.slate;
  return (
    <button
      onClick={onClick}
      className="w-full inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1.5 pr-3.5 py-1.5 hover:border-slate-400 active:scale-95 transition-all shadow-sm"
      data-testid={`home-v2-pill-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className={`w-7 h-7 rounded-full ${t.disc} flex items-center justify-center flex-shrink-0`}>
        <Icon weight="duotone" className="w-4 h-4" />
      </span>
      <span className="text-[12px] font-black text-slate-800 whitespace-nowrap flex-1 text-left truncate">{label}</span>
      {count != null && (
        <span className={`text-[10px] font-black ${t.badge} rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center flex-shrink-0`}>
          {count}
        </span>
      )}
    </button>
  );
}

// ─── TASKS tile (progress ring, coverage tasks) ───────────────────────
function TasksTile({ done, total, inProgress, onClick }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const dash = 2 * Math.PI * 18; // r=18
  const filled = (pct / 100) * dash;
  return (
    <button
      onClick={onClick}
      className="relative bg-white border border-slate-200 rounded-2xl p-3.5 text-left active:scale-[0.98] transition-transform shadow-sm hover:border-slate-300 flex items-center gap-3"
      data-testid="home-v2-tasks-tile"
    >
      <div className="relative flex-shrink-0 w-12 h-12">
        <svg viewBox="0 0 44 44" className="w-12 h-12 -rotate-90">
          <circle cx="22" cy="22" r="18" fill="none" stroke="#e2e8f0" strokeWidth="4" />
          <circle
            cx="22" cy="22" r="18" fill="none"
            stroke="#10b981" strokeWidth="4" strokeLinecap="round"
            strokeDasharray={`${filled} ${dash}`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <ListChecks weight="duotone" className="w-4 h-4 text-slate-700" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Tasks</p>
        <p className="text-sm font-black text-slate-900 leading-tight mt-0.5">
          {total > 0 ? `${done}/${total} done` : "No tasks yet"}
        </p>
        {inProgress > 0 && (
          <p className="text-[10px] text-emerald-600 font-bold leading-tight mt-0.5">
            {inProgress} in progress
          </p>
        )}
      </div>
    </button>
  );
}

// ─── DAILY PASSCODE tile ──────────────────────────────────────────────
function PasscodeTile({ ready, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`relative rounded-2xl p-3.5 text-left active:scale-[0.98] transition-transform shadow-sm flex items-center gap-3 border ${
        ready
          ? "bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-transparent"
          : "bg-white text-slate-900 border-slate-200 hover:border-slate-300"
      }`}
      data-testid="home-v2-passcode-tile"
    >
      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
        ready ? "bg-white/15" : "bg-slate-100"
      }`}>
        <LockKey weight="duotone" className={`w-5 h-5 ${ready ? "text-white" : "text-slate-600"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[10px] font-black uppercase tracking-widest ${
          ready ? "text-white/70" : "text-slate-400"
        }`}>Daily passcode</p>
        <p className="text-sm font-black leading-tight mt-0.5">
          {ready ? "Ready — tap to show" : "Tap to scan"}
        </p>
      </div>
    </button>
  );
}

// ─── HIGH FIVES tile ──────────────────────────────────────────────────
// Promoted to the top tile slot 2026-10-04 to encourage peer recognition.
// Always-on rose gradient so it reads as the inviting, celebratory CTA.
function HighFiveTile({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="relative rounded-2xl p-3.5 text-left active:scale-[0.98] transition-transform shadow-sm flex items-center gap-3 border border-transparent bg-gradient-to-br from-rose-500 to-pink-600 text-white"
      data-testid="home-v2-highfive-tile"
    >
      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-white/15">
        <HandHeart weight="duotone" className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-black uppercase tracking-widest text-white/70">High fives</p>
        <p className="text-sm font-black leading-tight mt-0.5">Recognize a teammate</p>
      </div>
    </button>
  );
}

